import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Tab,
  Tabs,
  Alert,
  CircularProgress,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Divider,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Psychology as PsychologyIcon,
  Chat as ChatIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Schedule as ScheduleIcon,
  CheckCircle as ApprovedIcon,
  Cancel as DeniedIcon,
  HourglassEmpty as PendingIcon,
  Warning as WarningIcon,
  Speed as SpeedIcon,
  Group as GroupIcon,
  Help as HelpIcon,
  Info as InfoIcon,
  SmartToy as AIIcon
} from '@mui/icons-material';
import uscisApplicationTrackingService, {
  ApplicationType,
  ApplicationStatus,
  ProcessingCenter,
  Priority,
  BenefitApplication,
  ApplicationMetrics,
  ProcessingTrends,
  BacklogAnalysis,
  MLInsights,
  BusinessQuestion,
  DashboardConfig
} from '../services/uscisApplicationTrackingService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`uscis-tabpanel-${index}`}
      aria-labelledby={`uscis-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const USCISApplicationTracking: React.FC = () => {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  
  // Data states
  const [metrics, setMetrics] = useState<ApplicationMetrics | null>(null);
  const [trends, setTrends] = useState<ProcessingTrends[]>([]);
  const [backlogAnalysis, setBacklogAnalysis] = useState<BacklogAnalysis | null>(null);
  const [mlInsights, setMLInsights] = useState<MLInsights | null>(null);
  const [applications, setApplications] = useState<BenefitApplication[]>([]);
  
  // Chat/Query states
  const [chatQuery, setChatQuery] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatResponse, setChatResponse] = useState<{
    answer: string;
    confidence?: number;
    sources?: string[];
    data?: any;
    suggestions?: string[];
    timestamp?: string;
  } | null>(null);
  const [showQuestionBrowser, setShowQuestionBrowser] = useState(false);
  const [questionCategories, setQuestionCategories] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sampleQuestions, setSampleQuestions] = useState<any[]>([]);
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<ApplicationStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<ApplicationType | ''>('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');

  // Filtered applications based on search and filter criteria
  const filteredApplications = applications.filter((app) => {
    // Enhanced search filter - check receipt number, application type, processing center, and date
    const searchLower = searchTerm.toLowerCase();
    const appReceivedDate = new Date(app.receivedDate).toLocaleDateString();
    const matchesSearch = searchTerm === '' || 
      app.receiptNumber.toLowerCase().includes(searchLower) ||
      app.applicationType.toLowerCase().includes(searchLower) ||
      app.processingCenter.toLowerCase().includes(searchLower) ||
      appReceivedDate.includes(searchTerm);
    
    // Status filter
    const matchesStatus = statusFilter === '' || app.currentStatus === statusFilter;
    
    // Type filter (if needed in the future)
    const matchesType = typeFilter === '' || app.applicationType === typeFilter;
    
    // Date range filter
    const appDate = new Date(app.receivedDate);
    const fromDate = dateFromFilter ? new Date(dateFromFilter) : null;
    const toDate = dateToFilter ? new Date(dateToFilter + 'T23:59:59') : null; // Include full day
    
    const matchesDateRange = (!fromDate || appDate >= fromDate) && 
                            (!toDate || appDate <= toDate);
    
    return matchesSearch && matchesStatus && matchesType && matchesDateRange;
  });

  // Helper function to highlight search terms
  const highlightSearchTerm = (text: string, searchTerm: string) => {
    if (!searchTerm) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <Box component="span" key={index} sx={{ backgroundColor: '#fff3cd', fontWeight: 'bold' }}>
          {part}
        </Box>
      ) : part
    );
  };

  useEffect(() => {
    loadDashboardData();
    loadQuestionCategories();
    // Load initial sample questions
    loadSampleQuestions();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      loadSampleQuestions();
    }
  }, [selectedCategory]);

  // Keyboard shortcuts for better UX
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Escape key to clear filters
      if (event.key === 'Escape' && (searchTerm || statusFilter || dateFromFilter || dateToFilter)) {
        setSearchTerm('');
        setStatusFilter('');
        setDateFromFilter('');
        setDateToFilter('');
        event.preventDefault();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchTerm, statusFilter, dateFromFilter, dateToFilter]);

  // Chat query handler
  const handleChatQuery = async () => {
    if (!chatQuery.trim()) return;
    
    setChatLoading(true);
    try {
      // Simulate AI processing with actual data analysis
      const response = await processNaturalLanguageQuery(chatQuery, applications);
      setChatResponse(response);
    } catch (error) {
      console.error('Chat query failed:', error);
      setChatResponse({
        answer: 'I apologize, but I encountered an error processing your question. Please try rephrasing your query or contact support if the issue persists.',
        confidence: 0.1
      });
    } finally {
      setChatLoading(false);
    }
  };

  // Process natural language queries with real data
  const processNaturalLanguageQuery = async (query: string, apps: BenefitApplication[]) => {
    const queryLower = query.toLowerCase();
    
    // Simple pattern matching for common queries
    if (queryLower.includes('how many') && queryLower.includes('received')) {
      const count = apps.length;
      return {
        answer: `There are currently ${count.toLocaleString()} applications in the system.`,
        confidence: 0.9,
        data: { totalApplications: count }
      };
    }
    
    if (queryLower.includes('average processing time')) {
      const completedApps = apps.filter(app => app.processingTimeBusinessDays && app.processingTimeBusinessDays > 0);
      const avgTime = completedApps.length > 0 ? 
        Math.round(completedApps.reduce((sum, app) => sum + (app.processingTimeBusinessDays || 0), 0) / completedApps.length) : 0;
      
      return {
        answer: `The average processing time across all completed applications is ${avgTime} business days.`,
        confidence: 0.85,
        data: { averageProcessingDays: avgTime, completedApplications: completedApps.length }
      };
    }
    
    if (queryLower.includes('backlog')) {
      const pendingApps = apps.filter(app => 
        !['Case Was Approved', 'Case Was Denied', 'Case Was Withdrawn', 'Case Was Terminated'].includes(app.currentStatus)
      );
      
      return {
        answer: `There are currently ${pendingApps.length.toLocaleString()} applications in the backlog, representing ${((pendingApps.length / apps.length) * 100).toFixed(1)}% of all applications.`,
        confidence: 0.9,
        data: { backlogSize: pendingApps.length, backlogPercentage: (pendingApps.length / apps.length) * 100 }
      };
    }
    
    // Default response for unrecognized queries
    return {
      answer: 'I can help you with questions about application counts, processing times, backlogs, approval rates, and processing center performance. Please try asking about specific metrics or trends.',
      confidence: 0.3,
      suggestions: [
        'How many applications were received this month?',
        'What is the average processing time for I-485 applications?',
        'How many applications are in the current backlog?',
        'What is the approval rate for naturalization applications?'
      ]
    };
  };

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [
        metricsData,
        trendsData,
        backlogData,
        mlData,
        applicationsData
      ] = await Promise.all([
        uscisApplicationTrackingService.getApplicationMetrics(),
        uscisApplicationTrackingService.getProcessingTrends('monthly'),
        uscisApplicationTrackingService.getBacklogAnalysis(),
        uscisApplicationTrackingService.getMLInsights(),
        uscisApplicationTrackingService.getApplications({ limit: 100 })
      ]);
      
      setMetrics(metricsData);
      setTrends(trendsData);
      setBacklogAnalysis(backlogData);
      setMLInsights(mlData);
      setApplications(applicationsData.applications);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('API calls failed, generating mock data:', err);
      
      // Generate mock data when API calls fail
      const mockApplications = generateMockApplications(100);
      const mockMetrics = generateMockMetrics(mockApplications);
      
      setApplications(mockApplications);
      setMetrics(mockMetrics);
      setTrends([]);
      setBacklogAnalysis(null);
      setMLInsights(null);
      setLastRefresh(new Date());
    } finally {
      setLoading(false);
    }
  };

  // Generate mock applications with realistic processing times
  const generateMockApplications = (count: number): BenefitApplication[] => {
    const applications: BenefitApplication[] = [];
    const applicationTypes = Object.values(ApplicationType);
    const statuses = Object.values(ApplicationStatus);
    const centers = Object.values(ProcessingCenter);
    const priorities = Object.values(Priority);
    
    for (let i = 0; i < count; i++) {
      const receivedDate = new Date();
      receivedDate.setDate(receivedDate.getDate() - Math.floor(Math.random() * 365)); // Random date within last year
      
      const applicationType = applicationTypes[Math.floor(Math.random() * applicationTypes.length)];
      const currentStatus = statuses[Math.floor(Math.random() * statuses.length)];
      const processingCenter = centers[Math.floor(Math.random() * centers.length)];
      
      // Calculate realistic processing times
      const daysSinceReceived = Math.floor((new Date().getTime() - receivedDate.getTime()) / (1000 * 60 * 60 * 24));
      let processingTimeBusinessDays: number | undefined;
      
      // For completed cases, assign a processing time
      if (['Case Was Approved', 'Case Was Denied', 'Case Was Withdrawn', 'Case Was Terminated'].includes(currentStatus)) {
        processingTimeBusinessDays = Math.floor(Math.random() * daysSinceReceived) + 30; // 30+ days minimum
      }
      
      const receiptNumber = generateReceiptNumber(processingCenter, i);
      
      applications.push({
        receiptNumber,
        applicationType,
        currentStatus,
        priority: priorities[Math.floor(Math.random() * priorities.length)],
        processingCenter,
        receivedDate: receivedDate.toISOString(),
        lastUpdatedDate: new Date().toISOString(),
        applicantId: `APP${String(i + 1).padStart(6, '0')}`,
        applicationChannel: ['Online', 'Mail', 'In-Person'][Math.floor(Math.random() * 3)] as 'Online' | 'Mail' | 'In-Person',
        processingTimeBusinessDays,
        totalStepsCompleted: Math.floor(Math.random() * 8) + 1,
        totalStepsRequired: 10,
        hasRFE: Math.random() > 0.7,
        hasInterview: Math.random() > 0.6,
        isExpedited: Math.random() > 0.9,
        hasComplications: Math.random() > 0.8,
        riskScore: Math.random(),
        predictedProcessingDays: Math.floor(Math.random() * 200) + 50,
        anomalyFlags: Math.random() > 0.8 ? ['unusual_delay', 'high_complexity'] : [],
        confidenceScore: Math.random() * 0.3 + 0.7
      });
    }
    
    return applications;
  };

  // Generate receipt number based on processing center
  const generateReceiptNumber = (center: ProcessingCenter, index: number): string => {
    const prefixes: { [key in ProcessingCenter]: string } = {
      [ProcessingCenter.NBC]: 'NBC',
      [ProcessingCenter.TSC]: 'TSC',
      [ProcessingCenter.NSC]: 'NSC',
      [ProcessingCenter.VSC]: 'VSC',
      [ProcessingCenter.CSC]: 'CSC',
      [ProcessingCenter.POTOMAC]: 'POT',
      [ProcessingCenter.LOCKBOX]: 'LBX'
    };
    
    const prefix = prefixes[center];
    const year = new Date().getFullYear().toString().slice(-2);
    const sequence = String(index + 1).padStart(8, '0');
    
    return `${prefix}${year}${sequence}`;
  };

  // Generate mock metrics from applications
  const generateMockMetrics = (apps: BenefitApplication[]): ApplicationMetrics => {
    const completedApps = apps.filter(app => 
      ['Case Was Approved', 'Case Was Denied'].includes(app.currentStatus)
    );
    
    const approvedApps = apps.filter(app => app.currentStatus === 'Case Was Approved');
    const avgProcessingTime = completedApps.length > 0 ? 
      Math.round(completedApps.reduce((sum, app) => sum + (app.processingTimeBusinessDays || 0), 0) / completedApps.length) : 0;
    
    return {
      totalApplications: apps.length,
      applicationsByType: {},
      applicationsByStatus: {},
      applicationsByCenter: {},
      averageProcessingTime: avgProcessingTime,
      backlogCount: apps.filter(app => 
        !['Case Was Approved', 'Case Was Denied', 'Case Was Withdrawn', 'Case Was Terminated'].includes(app.currentStatus)
      ).length,
      completionRate: completedApps.length / apps.length,
      expeditedCount: apps.filter(app => app.isExpedited).length,
      rfeRate: apps.filter(app => app.hasRFE).length / apps.length,
      approvalRate: approvedApps.length / completedApps.length,
      denialRate: apps.filter(app => app.currentStatus === 'Case Was Denied').length / completedApps.length
    };
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const loadQuestionCategories = async () => {
    try {
      console.log('Loading question categories...');
      const response = await fetch('/api/v1/uscis-tracking/question-categories');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('Categories loaded:', data);
      setQuestionCategories([
        { name: 'all', displayName: 'All Categories', count: data.totalQuestions },
        ...data.categories
      ]);
    } catch (error) {
      console.error('Error loading question categories:', error);
      // Fallback categories
      setQuestionCategories([
        { name: 'all', displayName: 'All Categories', count: 254 },
        { name: 'family_immigration', displayName: 'Family Immigration', count: 41 },
        { name: 'asylum_refugee', displayName: 'Asylum & Refugee', count: 40 },
        { name: 'naturalization', displayName: 'Naturalization', count: 22 },
        { name: 'green_card', displayName: 'Green Card', count: 18 },
        { name: 'daca_tps', displayName: 'DACA & TPS', count: 18 },
        { name: 'work_authorization', displayName: 'Work Authorization', count: 15 },
        { name: 'biometrics', displayName: 'Biometrics', count: 13 },
        { name: 'travel_documents', displayName: 'Travel Documents', count: 12 },
        { name: 'fees_payments', displayName: 'Fees & Payments', count: 12 },
        { name: 'processing_times', displayName: 'Processing Times', count: 10 },
        { name: 'application_status', displayName: 'Application Status', count: 10 },
        { name: 'contact_support', displayName: 'Contact & Support', count: 10 }
      ]);
    }
  };

  const loadSampleQuestions = async () => {
    try {
      console.log('Loading sample questions for category:', selectedCategory);
      const response = await fetch(`/api/v1/uscis-tracking/sample-questions?category=${selectedCategory}&limit=20`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('Sample questions loaded:', data);
      setSampleQuestions(data.questions || []);
    } catch (error) {
      console.error('Error loading sample questions:', error);
      // Comprehensive fallback questions based on category (64 total from expanded collection)
      const allQuestions = [
        // Application Status (4 questions)
        { question: "How Do I Check My Application Status?", category: "application_status", confidence: 0.95 },
        { question: "What Does My Case Status Mean?", category: "application_status", confidence: 0.95 },
        { question: "Why Hasn't My Status Updated?", category: "application_status", confidence: 0.9 },
        { question: "How Often Does USCIS Update Status?", category: "application_status", confidence: 0.9 },
        
        // Processing Times (6 questions)
        { question: "What Are USCIS Processing Times For My Case?", category: "processing_times", confidence: 0.95 },
        { question: "How Long Does Naturalization Take?", category: "processing_times", confidence: 0.9 },
        { question: "Why Is My Case Taking So Long?", category: "processing_times", confidence: 0.9 },
        { question: "What Are Current Processing Times?", category: "processing_times", confidence: 0.95 },
        { question: "My Case Is Outside Processing Times?", category: "processing_times", confidence: 0.95 },
        { question: "Do Processing Times Include Weekends?", category: "processing_times", confidence: 0.9 },
        
        // Green Card (6 questions)
        { question: "I Lost My Green Card What Do I Do?", category: "green_card", confidence: 0.95 },
        { question: "How Do I Renew My Green Card?", category: "green_card", confidence: 0.95 },
        { question: "What Are The Fields In Green Card Form?", category: "green_card", confidence: 0.95 },
        { question: "Can I Travel With Expired Green Card?", category: "green_card", confidence: 0.9 },
        { question: "How Long Is Green Card Valid?", category: "green_card", confidence: 0.95 },
        { question: "How Do I Check The Status Of My Green Card Renewal?", category: "green_card", confidence: 0.95 },
        
        // Work Authorization (5 questions)
        { question: "How Do I Apply For A Work Permit?", category: "work_authorization", confidence: 0.95 },
        { question: "How Do I Renew My Work Permit?", category: "work_authorization", confidence: 0.95 },
        { question: "Can I Work Without Employment Authorization Document (EAD)?", category: "work_authorization", confidence: 0.9 },
        { question: "What Is Employment Authorization Document (EAD) Category?", category: "work_authorization", confidence: 0.9 },
        { question: "How Long Is Employment Authorization Document (EAD) Valid?", category: "work_authorization", confidence: 0.9 },
        
        // Biometrics (6 questions)
        { question: "When Is My Biometrics Appointment?", category: "biometrics", confidence: 0.95 },
        { question: "What If I Missed My Biometrics Appointment?", category: "biometrics", confidence: 0.9 },
        { question: "What To Bring To Biometrics Appointment?", category: "biometrics", confidence: 0.95 },
        { question: "Can I Reschedule Biometrics Appointment?", category: "biometrics", confidence: 0.9 },
        { question: "How Long After Biometrics Is Interview?", category: "biometrics", confidence: 0.9 },
        { question: "What If I Did Not Receive My Biometric Appointment Letter?", category: "biometrics", confidence: 0.95 },
        
        // Travel Documents (4 questions)
        { question: "Can I Travel While My Application Is Pending?", category: "travel_documents", confidence: 0.9 },
        { question: "How Do I Apply For Advance Parole?", category: "travel_documents", confidence: 0.95 },
        { question: "What Is Advance Parole?", category: "travel_documents", confidence: 0.95 },
        { question: "Can I Use Expired Advance Parole?", category: "travel_documents", confidence: 0.9 },
        
        // Fees & Payments (5 questions)
        { question: "How Do I Pay USCIS Fees?", category: "fees_payments", confidence: 0.95 },
        { question: "Can I Get A Fee Waiver?", category: "fees_payments", confidence: 0.9 },
        { question: "What If My Payment Is Rejected?", category: "fees_payments", confidence: 0.9 },
        { question: "How Much Are USCIS Fees?", category: "fees_payments", confidence: 0.9 },
        { question: "Can I Pay USCIS Fees In Installments?", category: "fees_payments", confidence: 0.9 },
        
        // Naturalization (4 questions)
        { question: "What Documents Do I Need For Naturalization?", category: "naturalization", confidence: 0.95 },
        { question: "How Do I Check My Eligibility For Citizenship?", category: "naturalization", confidence: 0.9 },
        { question: "What Is The Naturalization Test?", category: "naturalization", confidence: 0.95 },
        { question: "How Do I Check My Citizenship Interview Date?", category: "naturalization", confidence: 0.95 },
        
        // Contact & Support (2 questions)
        { question: "How Can I Contact A Live USCIS Agent?", category: "contact_support", confidence: 0.98 },
        { question: "USCIS Contact Information?", category: "contact_support", confidence: 0.98 },
        
        // Additional categories with fewer questions
        { question: "How Do I Obtain My USCIS Receipt Number?", category: "receipt_number", confidence: 0.98 },
        { question: "What Is Request for Evidence (RFE)?", category: "rfe", confidence: 0.98 },
        { question: "How Do I Change My Address With USCIS?", category: "address_change", confidence: 0.95 },
        { question: "How Do I Renew My DACA?", category: "daca", confidence: 0.95 },
        { question: "Where Can I Get Immigration Forms?", category: "forms", confidence: 0.98 }
      ];
      
      // Filter by category if not 'all'
      const filteredQuestions = selectedCategory === 'all' 
        ? allQuestions 
        : allQuestions.filter(q => q.category === selectedCategory);
      
      setSampleQuestions(filteredQuestions);
    }
  };


  const getStatusColor = (status: ApplicationStatus) => {
    switch (status) {
      case ApplicationStatus.APPROVED:
        return 'success';
      case ApplicationStatus.DENIED:
        return 'error';
      case ApplicationStatus.UNDER_REVIEW:
        return 'info';
      case ApplicationStatus.RFE_SENT:
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusChipSx = (status: ApplicationStatus) => {
    const baseStyle = {
      fontWeight: 600,
      fontSize: '0.75rem'
    };
    
    switch (status) {
      case ApplicationStatus.APPROVED:
        return {
          ...baseStyle,
          backgroundColor: '#1b5e20',
          color: 'white',
          '& .MuiChip-icon': { color: 'white' }
        };
      case ApplicationStatus.DENIED:
        return {
          ...baseStyle,
          backgroundColor: '#c62828',
          color: 'white',
          '& .MuiChip-icon': { color: 'white' }
        };
      case ApplicationStatus.UNDER_REVIEW:
        return {
          ...baseStyle,
          backgroundColor: '#0277bd',
          color: 'white',
          '& .MuiChip-icon': { color: 'white' }
        };
      case ApplicationStatus.RFE_SENT:
        return {
          ...baseStyle,
          backgroundColor: '#e65100',
          color: 'white',
          '& .MuiChip-icon': { color: 'white' }
        };
      default:
        return {
          ...baseStyle,
          backgroundColor: '#424242',
          color: 'white',
          '& .MuiChip-icon': { color: 'white' }
        };
    }
  };

  const getStatusIcon = (status: ApplicationStatus) => {
    switch (status) {
      case ApplicationStatus.APPROVED:
        return <ApprovedIcon />;
      case ApplicationStatus.DENIED:
        return <DeniedIcon />;
      case ApplicationStatus.UNDER_REVIEW:
        return <PendingIcon />;
      default:
        return <ScheduleIcon />;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Loading USCIS Application Tracking Dashboard...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={loadDashboardData}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#003366' }}>
            USCIS Benefit Application Tracking
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Last updated: {lastRefresh.toLocaleString()}
            </Typography>
            <Tooltip title="Refresh Data">
              <IconButton onClick={loadDashboardData} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export Data">
              <IconButton color="primary">
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Typography variant="subtitle1" color="text.secondary">
          Real-time tracking and analytics for benefit application processing with AI-powered insights
        </Typography>
      </Box>

      {/* Key Metrics Cards */}
      {metrics && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #003366 0%, #004080 100%)', 
              color: 'white',
              border: '1px solid #e0e0e0'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 700,
                      color: 'white',
                      textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                    }}>
                      {metrics.totalApplications.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: 'rgba(255, 255, 255, 0.95)',
                      fontWeight: 500,
                      textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                    }}>
                      Total Applications
                    </Typography>
                  </Box>
                  <GroupIcon sx={{ 
                    fontSize: 40, 
                    color: 'rgba(255, 255, 255, 0.9)',
                    filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.3))'
                  }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)', 
              color: 'white',
              border: '1px solid #e0e0e0'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 700,
                      color: 'white',
                      textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                    }}>
                      {(metrics.approvalRate * 100).toFixed(1)}%
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: 'rgba(255, 255, 255, 0.95)',
                      fontWeight: 500,
                      textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                    }}>
                      Approval Rate
                    </Typography>
                  </Box>
                  <ApprovedIcon sx={{ 
                    fontSize: 40, 
                    color: 'rgba(255, 255, 255, 0.9)',
                    filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.3))'
                  }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #e65100 0%, #f57c00 100%)', 
              color: 'white',
              border: '1px solid #e0e0e0'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 700,
                      color: 'white',
                      textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                    }}>
                      {metrics.backlogCount.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: 'rgba(255, 255, 255, 0.95)',
                      fontWeight: 500,
                      textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                    }}>
                      Current Backlog
                    </Typography>
                  </Box>
                  <PendingIcon sx={{ 
                    fontSize: 40, 
                    color: 'rgba(255, 255, 255, 0.9)',
                    filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.3))'
                  }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #0d47a1 0%, #1976d2 100%)', 
              color: 'white',
              border: '1px solid #e0e0e0'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 700,
                      color: 'white',
                      textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                    }}>
                      {metrics.averageProcessingTime}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: 'rgba(255, 255, 255, 0.95)',
                      fontWeight: 500,
                      textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                    }}>
                      Avg Processing Days
                    </Typography>
                  </Box>
                  <SpeedIcon sx={{ 
                    fontSize: 40, 
                    color: 'rgba(255, 255, 255, 0.9)',
                    filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.3))'
                  }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs Navigation */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              minHeight: 64,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 500,
              color: '#003366',
              '&.Mui-selected': {
                color: '#003366',
                fontWeight: 600
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#003366',
              height: 3
            }
          }}
        >
          <Tab 
            icon={<DashboardIcon />} 
            label="Overview Dashboard" 
            iconPosition="start"
          />
          <Tab 
            icon={<TrendingUpIcon />} 
            label="Processing Trends" 
            iconPosition="start"
          />
          <Tab 
            icon={<AssessmentIcon />} 
            label="Backlog Analysis" 
            iconPosition="start"
          />
          <Tab 
            icon={<AIIcon />} 
            label="AI/ML Insights" 
            iconPosition="start"
          />
          <Tab 
            icon={<ChatIcon />} 
            label="DHS Chat" 
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={currentTab} index={0}>
        {/* Overview Dashboard */}
        <Grid container spacing={3}>
          {/* Application Types Breakdown */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Applications by Type
                </Typography>
                {metrics && Object.entries(metrics.applicationsByType).map(([type, count]) => (
                  <Box key={type} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Button
                        variant="text"
                        onClick={() => navigate(`/admin/uscis-application-tracking/${type}`)}
                        sx={{
                          textTransform: 'none',
                          color: '#003366',
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: 'rgba(0, 51, 102, 0.04)',
                            textDecoration: 'underline'
                          },
                          justifyContent: 'flex-start',
                          flexGrow: 1,
                          textAlign: 'left',
                          padding: 0
                        }}
                        aria-label={`View ${type} applications detail`}
                      >
                        <Typography variant="body2">{type}</Typography>
                      </Button>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {count?.toLocaleString()}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(count || 0) / metrics.totalApplications * 100}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>

          {/* Processing Centers */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Processing Centers
                </Typography>
                {metrics && Object.entries(metrics.applicationsByCenter).map(([center, count]) => (
                  <Box key={center} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">{center}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {count?.toLocaleString()}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={(count || 0) / metrics.totalApplications * 100}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>

          {/* Recent Applications */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Recent Applications
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Showing {Math.min(10, filteredApplications.length)} of {filteredApplications.length} applications
                      {(searchTerm || statusFilter || dateFromFilter || dateToFilter) && ` (filtered from ${applications.length} total)`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      size="small"
                      placeholder="Search receipt number, type, center, or date..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ minWidth: 250 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={statusFilter}
                        label="Status"
                        onChange={(e) => setStatusFilter(e.target.value as ApplicationStatus)}
                      >
                        <MenuItem value="">All Statuses</MenuItem>
                        {Object.values(ApplicationStatus).map((status) => (
                          <MenuItem key={status} value={status}>{status}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {(searchTerm || statusFilter || dateFromFilter || dateToFilter) && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setSearchTerm('');
                          setStatusFilter('');
                          setDateFromFilter('');
                          setDateToFilter('');
                        }}
                        sx={{ 
                          color: '#003366',
                          borderColor: '#003366',
                          '&:hover': {
                            backgroundColor: '#003366',
                            color: 'white'
                          }
                        }}
                      >
                        Clear Filters
                      </Button>
                    )}
                  </Box>
                </Box>
                
                {/* Date Range Filters */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 'auto' }}>
                    Date Range:
                  </Typography>
                  <TextField
                    size="small"
                    type="date"
                    label="From Date"
                    value={dateFromFilter}
                    onChange={(e) => setDateFromFilter(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 150 }}
                  />
                  <TextField
                    size="small"
                    type="date"
                    label="To Date"
                    value={dateToFilter}
                    onChange={(e) => setDateToFilter(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 150 }}
                  />
                  {(dateFromFilter || dateToFilter) && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => {
                        setDateFromFilter('');
                        setDateToFilter('');
                      }}
                      sx={{ 
                        color: '#003366',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 51, 102, 0.04)'
                        }
                      }}
                    >
                      Clear Dates
                    </Button>
                  )}
                  <Box sx={{ flexGrow: 1 }} />
                  {/* Quick Date Filters */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        const today = new Date();
                        const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
                        setDateFromFilter(lastWeek.toISOString().split('T')[0]);
                        setDateToFilter(today.toISOString().split('T')[0]);
                      }}
                      sx={{ 
                        color: '#003366',
                        borderColor: '#003366',
                        fontSize: '0.75rem',
                        '&:hover': {
                          backgroundColor: '#003366',
                          color: 'white'
                        }
                      }}
                    >
                      Last 7 Days
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        const today = new Date();
                        const lastMonth = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
                        setDateFromFilter(lastMonth.toISOString().split('T')[0]);
                        setDateToFilter(today.toISOString().split('T')[0]);
                      }}
                      sx={{ 
                        color: '#003366',
                        borderColor: '#003366',
                        fontSize: '0.75rem',
                        '&:hover': {
                          backgroundColor: '#003366',
                          color: 'white'
                        }
                      }}
                    >
                      Last 30 Days
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        const today = new Date();
                        const thisYear = new Date(today.getFullYear(), 0, 1);
                        setDateFromFilter(thisYear.toISOString().split('T')[0]);
                        setDateToFilter(today.toISOString().split('T')[0]);
                      }}
                      sx={{ 
                        color: '#003366',
                        borderColor: '#003366',
                        fontSize: '0.75rem',
                        '&:hover': {
                          backgroundColor: '#003366',
                          color: 'white'
                        }
                      }}
                    >
                      This Year
                    </Button>
                  </Box>
                </Box>
                
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Receipt Number</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Received Date</TableCell>
                        <TableCell>Processing Days</TableCell>
                        <TableCell>Center</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredApplications.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                              <SearchIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                              <Typography variant="h6" color="text.secondary">
                                No applications found
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {searchTerm || statusFilter || dateFromFilter || dateToFilter
                                  ? 'Try adjusting your search criteria or filters'
                                  : 'No applications available to display'
                                }
                              </Typography>
                              {(searchTerm || statusFilter || dateFromFilter || dateToFilter) && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => {
                                    setSearchTerm('');
                                    setStatusFilter('');
                                    setDateFromFilter('');
                                    setDateToFilter('');
                                  }}
                                  sx={{ 
                                    color: '#003366',
                                    borderColor: '#003366',
                                    '&:hover': {
                                      backgroundColor: '#003366',
                                      color: 'white'
                                    }
                                  }}
                                >
                                  Clear All Filters
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredApplications.slice(0, 10).map((app) => (
                          <TableRow key={app.receiptNumber} hover>
                            <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                              {highlightSearchTerm(app.receiptNumber, searchTerm)}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={highlightSearchTerm(app.applicationType, searchTerm)} 
                                size="small" 
                                sx={{
                                  backgroundColor: '#003366',
                                  color: 'white',
                                  fontWeight: 600,
                                  fontSize: '0.75rem'
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip 
                                icon={getStatusIcon(app.currentStatus)}
                                label={app.currentStatus}
                                size="small"
                                sx={getStatusChipSx(app.currentStatus)}
                              />
                            </TableCell>
                            <TableCell>
                              {highlightSearchTerm(new Date(app.receivedDate).toLocaleDateString(), searchTerm)}
                            </TableCell>
                            <TableCell>
                              {(() => {
                                // Calculate processing days based on received date and current status
                                const receivedDate = new Date(app.receivedDate);
                                const currentDate = new Date();
                                const daysDiff = Math.floor((currentDate.getTime() - receivedDate.getTime()) / (1000 * 60 * 60 * 24));
                                
                                // If case is completed, use actual processing time or calculated days
                                if (['Case Was Approved', 'Case Was Denied', 'Case Was Withdrawn', 'Case Was Terminated'].includes(app.currentStatus)) {
                                  return app.processingTimeBusinessDays || daysDiff;
                                }
                                
                                // For pending cases, show current days in system
                                return daysDiff;
                              })()}
                            </TableCell>
                            <TableCell>
                              {highlightSearchTerm(app.processingCenter, searchTerm)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        {/* Processing Trends */}
        <Grid container spacing={3}>
          {/* Trend Summary Cards */}
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUpIcon sx={{ color: '#2e7d32', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Monthly Received
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ color: '#2e7d32', fontWeight: 700, mb: 1 }}>
                  {applications.filter(app => {
                    const appDate = new Date(app.receivedDate);
                    const now = new Date();
                    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
                    return appDate >= thisMonth;
                  }).length.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Applications this month
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ApprovedIcon sx={{ color: '#1976d2', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Monthly Approved
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ color: '#1976d2', fontWeight: 700, mb: 1 }}>
                  {applications.filter(app => {
                    const appDate = new Date(app.actualCompletionDate || app.receivedDate);
                    const now = new Date();
                    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
                    return app.currentStatus === 'Case Was Approved' && appDate >= thisMonth;
                  }).length.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Approvals this month
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SpeedIcon sx={{ color: '#f57c00', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Avg Processing Time
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ color: '#f57c00', fontWeight: 700, mb: 1 }}>
                  {Math.round(applications.filter(app => app.processingTimeBusinessDays && app.processingTimeBusinessDays > 0)
                    .reduce((sum, app) => sum + (app.processingTimeBusinessDays || 0), 0) / 
                    applications.filter(app => app.processingTimeBusinessDays && app.processingTimeBusinessDays > 0).length || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Days average
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PendingIcon sx={{ color: '#d32f2f', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Pending Cases
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ color: '#d32f2f', fontWeight: 700, mb: 1 }}>
                  {applications.filter(app => 
                    !['Case Was Approved', 'Case Was Denied', 'Case Was Withdrawn', 'Case Was Terminated'].includes(app.currentStatus)
                  ).length.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active applications
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Monthly Trends Chart */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Monthly Processing Trends
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip label="Received" size="small" sx={{ backgroundColor: '#2e7d32', color: 'white' }} />
                    <Chip label="Approved" size="small" sx={{ backgroundColor: '#1976d2', color: 'white' }} />
                    <Chip label="Denied" size="small" sx={{ backgroundColor: '#d32f2f', color: 'white' }} />
                    <Chip label="Pending" size="small" sx={{ backgroundColor: '#f57c00', color: 'white' }} />
                  </Box>
                </Box>
                
                {/* Trend Analysis */}
                <Box sx={{ mb: 3 }}>
                  {(() => {
                    // Calculate monthly trends
                    const monthlyData: { [key: string]: { month: string; received: number; approved: number; denied: number; pending: number } } = {};
                    const now = new Date();
                    
                    // Initialize last 12 months
                    for (let i = 11; i >= 0; i--) {
                      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
                      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                      monthlyData[key] = {
                        month: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
                        received: 0,
                        approved: 0,
                        denied: 0,
                        pending: 0
                      };
                    }
                    
                    // Count applications by month
                    applications.forEach(app => {
                      const receivedDate = new Date(app.receivedDate);
                      const receivedKey = `${receivedDate.getFullYear()}-${String(receivedDate.getMonth() + 1).padStart(2, '0')}`;
                      
                      if (monthlyData[receivedKey]) {
                        monthlyData[receivedKey].received++;
                        
                        if (app.currentStatus === 'Case Was Approved') {
                          monthlyData[receivedKey].approved++;
                        } else if (app.currentStatus === 'Case Was Denied') {
                          monthlyData[receivedKey].denied++;
                        } else if (!['Case Was Withdrawn', 'Case Was Terminated'].includes(app.currentStatus)) {
                          monthlyData[receivedKey].pending++;
                        }
                      }
                    });
                    
                    const months = Object.values(monthlyData);
                    
                    return (
                      <Box sx={{ height: 400 }}>
                        {/* Simple Bar Chart Visualization */}
                        <Box sx={{ display: 'flex', alignItems: 'end', height: '100%', gap: 1, px: 2 }}>
                          {months.map((month, index) => {
                            const maxValue = Math.max(...months.map(m => m.received + m.approved + m.denied + m.pending));
                            const totalHeight = 300;
                            
                            const receivedHeight = (month.received / maxValue) * totalHeight;
                            const approvedHeight = (month.approved / maxValue) * totalHeight;
                            const deniedHeight = (month.denied / maxValue) * totalHeight;
                            const pendingHeight = (month.pending / maxValue) * totalHeight;
                            
                            return (
                              <Box key={index} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                {/* Stacked bars */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1 }}>
                                  <Tooltip title={`Received: ${month.received}`}>
                                    <Box sx={{
                                      width: 20,
                                      height: Math.max(receivedHeight, 2),
                                      backgroundColor: '#2e7d32',
                                      mb: 0.5,
                                      borderRadius: '2px 2px 0 0'
                                    }} />
                                  </Tooltip>
                                  <Tooltip title={`Approved: ${month.approved}`}>
                                    <Box sx={{
                                      width: 20,
                                      height: Math.max(approvedHeight, 1),
                                      backgroundColor: '#1976d2',
                                      mb: 0.5
                                    }} />
                                  </Tooltip>
                                  <Tooltip title={`Denied: ${month.denied}`}>
                                    <Box sx={{
                                      width: 20,
                                      height: Math.max(deniedHeight, 1),
                                      backgroundColor: '#d32f2f',
                                      mb: 0.5
                                    }} />
                                  </Tooltip>
                                  <Tooltip title={`Pending: ${month.pending}`}>
                                    <Box sx={{
                                      width: 20,
                                      height: Math.max(pendingHeight, 1),
                                      backgroundColor: '#f57c00',
                                      borderRadius: '0 0 2px 2px'
                                    }} />
                                  </Tooltip>
                                </Box>
                                
                                {/* Month label */}
                                <Typography variant="caption" sx={{ 
                                  transform: 'rotate(-45deg)', 
                                  fontSize: '0.7rem',
                                  whiteSpace: 'nowrap',
                                  mt: 2
                                }}>
                                  {month.month}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })()}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Processing Time Trends by Application Type */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Processing Time by Application Type
                </Typography>
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {(() => {
                    const typeStats: { [key: string]: { count: number; totalDays: number; approved: number; denied: number; pending: number } } = {};
                    
                    applications.forEach(app => {
                      if (!typeStats[app.applicationType]) {
                        typeStats[app.applicationType] = {
                          count: 0,
                          totalDays: 0,
                          approved: 0,
                          denied: 0,
                          pending: 0
                        };
                      }
                      
                      typeStats[app.applicationType].count++;
                      typeStats[app.applicationType].totalDays += app.processingTimeBusinessDays || 0;
                      
                      if (app.currentStatus === 'Case Was Approved') {
                        typeStats[app.applicationType].approved++;
                      } else if (app.currentStatus === 'Case Was Denied') {
                        typeStats[app.applicationType].denied++;
                      } else if (!['Case Was Withdrawn', 'Case Was Terminated'].includes(app.currentStatus)) {
                        typeStats[app.applicationType].pending++;
                      }
                    });
                    
                    return Object.entries(typeStats)
                      .sort(([,a], [,b]) => b.count - a.count)
                      .map(([type, stats]) => {
                        const avgDays = Math.round(stats.totalDays / stats.count);
                        const approvalRate = ((stats.approved / stats.count) * 100).toFixed(1);
                        
                        return (
                          <Box key={type} sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Chip 
                                label={type} 
                                sx={{ 
                                  backgroundColor: '#003366', 
                                  color: 'white',
                                  fontWeight: 600 
                                }} 
                              />
                              <Typography variant="body2" color="text.secondary">
                                {stats.count} applications
                              </Typography>
                            </Box>
                            
                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Avg Processing Time
                                </Typography>
                                <Typography variant="h6" sx={{ color: '#f57c00', fontWeight: 600 }}>
                                  {avgDays} days
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Approval Rate
                                </Typography>
                                <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                                  {approvalRate}%
                                </Typography>
                              </Grid>
                            </Grid>
                            
                            {/* Status breakdown */}
                            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                              <Chip 
                                label={`${stats.approved} Approved`} 
                                size="small" 
                                sx={{ backgroundColor: '#e8f5e8', color: '#2e7d32' }}
                              />
                              <Chip 
                                label={`${stats.denied} Denied`} 
                                size="small" 
                                sx={{ backgroundColor: '#ffebee', color: '#d32f2f' }}
                              />
                              <Chip 
                                label={`${stats.pending} Pending`} 
                                size="small" 
                                sx={{ backgroundColor: '#fff3e0', color: '#f57c00' }}
                              />
                            </Box>
                          </Box>
                        );
                      });
                  })()}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Processing Center Performance */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Processing Center Performance
                </Typography>
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {(() => {
                    const centerStats: { [key: string]: { count: number; totalDays: number; approved: number; denied: number; pending: number } } = {};
                    
                    applications.forEach(app => {
                      if (!centerStats[app.processingCenter]) {
                        centerStats[app.processingCenter] = {
                          count: 0,
                          totalDays: 0,
                          approved: 0,
                          denied: 0,
                          pending: 0
                        };
                      }
                      
                      centerStats[app.processingCenter].count++;
                      centerStats[app.processingCenter].totalDays += app.processingTimeBusinessDays || 0;
                      
                      if (app.currentStatus === 'Case Was Approved') {
                        centerStats[app.processingCenter].approved++;
                      } else if (app.currentStatus === 'Case Was Denied') {
                        centerStats[app.processingCenter].denied++;
                      } else if (!['Case Was Withdrawn', 'Case Was Terminated'].includes(app.currentStatus)) {
                        centerStats[app.processingCenter].pending++;
                      }
                    });
                    
                    return Object.entries(centerStats)
                      .sort(([,a], [,b]) => b.count - a.count)
                      .map(([center, stats]) => {
                        const avgDays = Math.round(stats.totalDays / stats.count);
                        const approvalRate = ((stats.approved / stats.count) * 100).toFixed(1);
                        
                        return (
                          <Box key={center} sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                              {center}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              {stats.count} applications processed
                            </Typography>
                            
                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Avg Processing Time
                                </Typography>
                                <Typography variant="h6" sx={{ color: '#f57c00', fontWeight: 600 }}>
                                  {avgDays} days
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Approval Rate
                                </Typography>
                                <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                                  {approvalRate}%
                                </Typography>
                              </Grid>
                            </Grid>
                            
                            {/* Performance indicator */}
                            <LinearProgress 
                              variant="determinate" 
                              value={Math.min(100, (stats.approved / stats.count) * 100)}
                              sx={{ 
                                mt: 2, 
                                height: 8, 
                                borderRadius: 4,
                                backgroundColor: '#e0e0e0',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: stats.approved / stats.count > 0.7 ? '#2e7d32' : 
                                                 stats.approved / stats.count > 0.5 ? '#f57c00' : '#d32f2f'
                                }
                              }} 
                            />
                          </Box>
                        );
                      });
                  })()}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={currentTab} index={2}>
        {/* Backlog Analysis */}
        <Grid container spacing={3}>
          {/* Backlog Summary Cards */}
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PendingIcon sx={{ color: '#f57c00', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Current Backlog
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ color: '#f57c00', fontWeight: 700, mb: 1 }}>
                  {applications.filter(app => 
                    !['Case Was Approved', 'Case Was Denied', 'Case Was Withdrawn', 'Case Was Terminated'].includes(app.currentStatus)
                  ).length.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Cases pending processing
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {((applications.filter(app => 
                      !['Case Was Approved', 'Case Was Denied', 'Case Was Withdrawn', 'Case Was Terminated'].includes(app.currentStatus)
                    ).length / applications.length) * 100).toFixed(1)}% of total cases
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <WarningIcon sx={{ color: '#d32f2f', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Oldest Case
                  </Typography>
                </Box>
                {(() => {
                  const pendingCases = applications.filter(app => 
                    !['Case Was Approved', 'Case Was Denied', 'Case Was Withdrawn', 'Case Was Terminated'].includes(app.currentStatus)
                  );
                  const oldestCase = pendingCases.reduce((oldest, app) => {
                    const appDate = new Date(app.receivedDate);
                    const oldestDate = new Date(oldest.receivedDate);
                    return appDate < oldestDate ? app : oldest;
                  }, pendingCases[0] || { receivedDate: new Date(), receiptNumber: 'N/A', applicationType: 'N/A' });
                  
                  const daysInSystem = Math.floor((new Date().getTime() - new Date(oldestCase.receivedDate).getTime()) / (1000 * 60 * 60 * 24));
                  
                  return (
                    <>
                      <Typography variant="h3" sx={{ color: '#d32f2f', fontWeight: 700, mb: 1 }}>
                        {daysInSystem}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Days in system
                      </Typography>
                      <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                        {oldestCase.receiptNumber} ({oldestCase.applicationType})
                      </Typography>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ScheduleIcon sx={{ color: '#2e7d32', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Avg Backlog Age
                  </Typography>
                </Box>
                {(() => {
                  const pendingCases = applications.filter(app => 
                    !['Case Was Approved', 'Case Was Denied', 'Case Was Withdrawn', 'Case Was Terminated'].includes(app.currentStatus)
                  );
                  const avgAge = pendingCases.length > 0 ? 
                    Math.round(pendingCases.reduce((sum, app) => {
                      const daysInSystem = Math.floor((new Date().getTime() - new Date(app.receivedDate).getTime()) / (1000 * 60 * 60 * 24));
                      return sum + daysInSystem;
                    }, 0) / pendingCases.length) : 0;
                  
                  return (
                    <>
                      <Typography variant="h3" sx={{ color: '#2e7d32', fontWeight: 700, mb: 1 }}>
                        {avgAge}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Days average
                      </Typography>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SpeedIcon sx={{ color: '#1976d2', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Processing Rate
                  </Typography>
                </Box>
                {(() => {
                  const completedCases = applications.filter(app => 
                    ['Case Was Approved', 'Case Was Denied'].includes(app.currentStatus)
                  );
                  const avgProcessingTime = completedCases.length > 0 ?
                    Math.round(completedCases.reduce((sum, app) => sum + (app.processingTimeBusinessDays || 0), 0) / completedCases.length) : 0;
                  
                  return (
                    <>
                      <Typography variant="h3" sx={{ color: '#1976d2', fontWeight: 700, mb: 1 }}>
                        {avgProcessingTime}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Days to complete
                      </Typography>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </Grid>

          {/* Backlog by Application Type */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Backlog by Application Type
                </Typography>
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {(() => {
                    const backlogByType: { [key: string]: { count: number; oldestDays: number; avgDays: number; totalDays: number } } = {};
                    
                    applications.forEach(app => {
                      if (!['Case Was Approved', 'Case Was Denied', 'Case Was Withdrawn', 'Case Was Terminated'].includes(app.currentStatus)) {
                        if (!backlogByType[app.applicationType]) {
                          backlogByType[app.applicationType] = {
                            count: 0,
                            oldestDays: 0,
                            avgDays: 0,
                            totalDays: 0
                          };
                        }
                        
                        const daysInSystem = Math.floor((new Date().getTime() - new Date(app.receivedDate).getTime()) / (1000 * 60 * 60 * 24));
                        backlogByType[app.applicationType].count++;
                        backlogByType[app.applicationType].totalDays += daysInSystem;
                        backlogByType[app.applicationType].oldestDays = Math.max(backlogByType[app.applicationType].oldestDays, daysInSystem);
                      }
                    });
                    
                    // Calculate averages
                    Object.keys(backlogByType).forEach(type => {
                      backlogByType[type].avgDays = Math.round(backlogByType[type].totalDays / backlogByType[type].count);
                    });
                    
                    return Object.entries(backlogByType)
                      .sort(([,a], [,b]) => b.count - a.count)
                      .map(([type, stats]) => (
                        <Box key={type} sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Chip 
                              label={type} 
                              sx={{ 
                                backgroundColor: '#003366', 
                                color: 'white',
                                fontWeight: 600 
                              }} 
                            />
                            <Typography variant="h6" sx={{ color: '#f57c00', fontWeight: 600 }}>
                              {stats.count} cases
                            </Typography>
                          </Box>
                          
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Average Age
                              </Typography>
                              <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                                {stats.avgDays} days
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Oldest Case
                              </Typography>
                              <Typography variant="h6" sx={{ color: '#d32f2f', fontWeight: 600 }}>
                                {stats.oldestDays} days
                              </Typography>
                            </Grid>
                          </Grid>
                          
                          {/* Priority indicator */}
                          <Box sx={{ mt: 2 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={Math.min(100, (stats.avgDays / 365) * 100)}
                              sx={{ 
                                height: 6, 
                                borderRadius: 3,
                                backgroundColor: '#e0e0e0',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: stats.avgDays > 180 ? '#d32f2f' : 
                                                 stats.avgDays > 90 ? '#f57c00' : '#2e7d32'
                                }
                              }} 
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              Priority: {stats.avgDays > 180 ? 'High' : stats.avgDays > 90 ? 'Medium' : 'Low'}
                            </Typography>
                          </Box>
                        </Box>
                      ));
                  })()}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Backlog by Processing Center */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Backlog by Processing Center
                </Typography>
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {(() => {
                    const backlogByCenter: { [key: string]: { count: number; oldestDays: number; avgDays: number; totalDays: number } } = {};
                    
                    applications.forEach(app => {
                      if (!['Case Was Approved', 'Case Was Denied', 'Case Was Withdrawn', 'Case Was Terminated'].includes(app.currentStatus)) {
                        if (!backlogByCenter[app.processingCenter]) {
                          backlogByCenter[app.processingCenter] = {
                            count: 0,
                            oldestDays: 0,
                            avgDays: 0,
                            totalDays: 0
                          };
                        }
                        
                        const daysInSystem = Math.floor((new Date().getTime() - new Date(app.receivedDate).getTime()) / (1000 * 60 * 60 * 24));
                        backlogByCenter[app.processingCenter].count++;
                        backlogByCenter[app.processingCenter].totalDays += daysInSystem;
                        backlogByCenter[app.processingCenter].oldestDays = Math.max(backlogByCenter[app.processingCenter].oldestDays, daysInSystem);
                      }
                    });
                    
                    // Calculate averages
                    Object.keys(backlogByCenter).forEach(center => {
                      backlogByCenter[center].avgDays = Math.round(backlogByCenter[center].totalDays / backlogByCenter[center].count);
                    });
                    
                    return Object.entries(backlogByCenter)
                      .sort(([,a], [,b]) => b.count - a.count)
                      .map(([center, stats]) => (
                        <Box key={center} sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            {center}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {stats.count} cases in backlog
                          </Typography>
                          
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Average Age
                              </Typography>
                              <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                                {stats.avgDays} days
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Oldest Case
                              </Typography>
                              <Typography variant="h6" sx={{ color: '#d32f2f', fontWeight: 600 }}>
                                {stats.oldestDays} days
                              </Typography>
                            </Grid>
                          </Grid>
                          
                          {/* Workload indicator */}
                          <Box sx={{ mt: 2 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={Math.min(100, (stats.count / Math.max(...Object.values(backlogByCenter).map(s => s.count))) * 100)}
                              sx={{ 
                                height: 6, 
                                borderRadius: 3,
                                backgroundColor: '#e0e0e0',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: '#1976d2'
                                }
                              }} 
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              Workload: {stats.count > 200 ? 'High' : stats.count > 100 ? 'Medium' : 'Low'}
                            </Typography>
                          </Box>
                        </Box>
                      ));
                  })()}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Backlog Trends Chart */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Backlog Age Distribution
                </Typography>
                
                {(() => {
                  const pendingCases = applications.filter(app => 
                    !['Case Was Approved', 'Case Was Denied', 'Case Was Withdrawn', 'Case Was Terminated'].includes(app.currentStatus)
                  );
                  
                  const ageRanges = {
                    '0-30 days': 0,
                    '31-60 days': 0,
                    '61-90 days': 0,
                    '91-180 days': 0,
                    '181-365 days': 0,
                    '365+ days': 0
                  };
                  
                  pendingCases.forEach(app => {
                    const daysInSystem = Math.floor((new Date().getTime() - new Date(app.receivedDate).getTime()) / (1000 * 60 * 60 * 24));
                    
                    if (daysInSystem <= 30) ageRanges['0-30 days']++;
                    else if (daysInSystem <= 60) ageRanges['31-60 days']++;
                    else if (daysInSystem <= 90) ageRanges['61-90 days']++;
                    else if (daysInSystem <= 180) ageRanges['91-180 days']++;
                    else if (daysInSystem <= 365) ageRanges['181-365 days']++;
                    else ageRanges['365+ days']++;
                  });
                  
                  const maxValue = Math.max(...Object.values(ageRanges));
                  const colors = ['#2e7d32', '#4caf50', '#8bc34a', '#ffc107', '#ff9800', '#d32f2f'];
                  
                  return (
                    <Box sx={{ height: 300 }}>
                      <Box sx={{ display: 'flex', alignItems: 'end', height: '100%', gap: 2, px: 2 }}>
                        {Object.entries(ageRanges).map(([range, count], index) => {
                          const height = maxValue > 0 ? (count / maxValue) * 250 : 0;
                          
                          return (
                            <Box key={range} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <Tooltip title={`${count} cases`}>
                                <Box sx={{
                                  width: '100%',
                                  height: Math.max(height, 2),
                                  backgroundColor: colors[index],
                                  mb: 1,
                                  borderRadius: '4px 4px 0 0',
                                  display: 'flex',
                                  alignItems: 'end',
                                  justifyContent: 'center',
                                  pb: 1
                                }}>
                                  <Typography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>
                                    {count}
                                  </Typography>
                                </Box>
                              </Tooltip>
                              
                              <Typography variant="caption" sx={{ 
                                fontSize: '0.7rem',
                                textAlign: 'center',
                                mt: 1
                              }}>
                                {range}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })()}
              </CardContent>
            </Card>
          </Grid>

          {/* Actionable Recommendations */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Actionable Recommendations
                </Typography>
                
                {(() => {
                  const pendingCases = applications.filter(app => 
                    !['Case Was Approved', 'Case Was Denied', 'Case Was Withdrawn', 'Case Was Terminated'].includes(app.currentStatus)
                  );
                  
                  const oldCases = pendingCases.filter(app => {
                    const daysInSystem = Math.floor((new Date().getTime() - new Date(app.receivedDate).getTime()) / (1000 * 60 * 60 * 24));
                    return daysInSystem > 180;
                  });
                  
                  const recommendations = [
                    {
                      priority: 'High',
                      title: 'Review Cases Over 180 Days',
                      description: `${oldCases.length} cases have been pending for over 6 months and require immediate attention`,
                      action: 'Assign dedicated review team',
                      color: '#d32f2f',
                      icon: <WarningIcon />
                    },
                    {
                      priority: 'Medium',
                      title: 'Optimize Processing Centers',
                      description: 'Redistribute workload to balance processing times across centers',
                      action: 'Implement load balancing',
                      color: '#f57c00',
                      icon: <SpeedIcon />
                    },
                    {
                      priority: 'Low',
                      title: 'Automate Routine Processes',
                      description: 'Implement automated screening for standard application types',
                      action: 'Deploy automation tools',
                      color: '#2e7d32',
                      icon: <AssessmentIcon />
                    }
                  ];
                  
                  return (
                    <Grid container spacing={2}>
                      {recommendations.map((rec, index) => (
                        <Grid item xs={12} md={4} key={index}>
                          <Box sx={{ 
                            p: 3, 
                            bgcolor: 'grey.50', 
                            borderRadius: 2,
                            borderLeft: `4px solid ${rec.color}`,
                            height: '100%'
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ color: rec.color, mr: 1 }}>
                                {rec.icon}
                              </Box>
                              <Chip 
                                label={rec.priority} 
                                size="small"
                                sx={{ 
                                  backgroundColor: rec.color,
                                  color: 'white',
                                  fontWeight: 600
                                }}
                              />
                            </Box>
                            
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                              {rec.title}
                            </Typography>
                            
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              {rec.description}
                            </Typography>
                            
                            <Button
                              variant="outlined"
                              size="small"
                              sx={{
                                color: rec.color,
                                borderColor: rec.color,
                                '&:hover': {
                                  backgroundColor: rec.color,
                                  color: 'white'
                                }
                              }}
                            >
                              {rec.action}
                            </Button>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  );
                })()}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={currentTab} index={3}>
        {/* AI/ML Insights */}
        <Grid container spacing={3}>
          {/* AI/ML Summary Cards */}
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AIIcon sx={{ color: '#1976d2', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Risk Score
                  </Typography>
                </Box>
                {(() => {
                  const avgRiskScore = applications.length > 0 ? 
                    (applications.reduce((sum, app) => sum + (app.riskScore || 0), 0) / applications.length) : 0;
                  
                  return (
                    <>
                      <Typography variant="h3" sx={{ color: '#1976d2', fontWeight: 700, mb: 1 }}>
                        {(avgRiskScore * 100).toFixed(1)}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Average risk level
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={avgRiskScore * 100}
                        sx={{ 
                          mt: 2, 
                          height: 6, 
                          borderRadius: 3,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: avgRiskScore > 0.7 ? '#d32f2f' : 
                                           avgRiskScore > 0.4 ? '#f57c00' : '#2e7d32'
                          }
                        }} 
                      />
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PsychologyIcon sx={{ color: '#9c27b0', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Predicted Delays
                  </Typography>
                </Box>
                {(() => {
                  const delayedCases = applications.filter(app => {
                    const predicted = app.predictedProcessingDays || 0;
                    const actual = app.processingTimeBusinessDays || 0;
                    return predicted > 0 && actual > predicted * 1.2;
                  });
                  
                  return (
                    <>
                      <Typography variant="h3" sx={{ color: '#9c27b0', fontWeight: 700, mb: 1 }}>
                        {delayedCases.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Cases exceeding predictions
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {((delayedCases.length / applications.length) * 100).toFixed(1)}% of total cases
                      </Typography>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SpeedIcon sx={{ color: '#ff5722', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Processing Forecast
                  </Typography>
                </Box>
                {(() => {
                  const avgPredicted = applications.filter(app => app.predictedProcessingDays && app.predictedProcessingDays > 0)
                    .reduce((sum, app) => sum + (app.predictedProcessingDays || 0), 0) / 
                    applications.filter(app => app.predictedProcessingDays && app.predictedProcessingDays > 0).length || 0;
                  
                  return (
                    <>
                      <Typography variant="h3" sx={{ color: '#ff5722', fontWeight: 700, mb: 1 }}>
                        {Math.round(avgPredicted)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Days predicted average
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Based on ML models
                      </Typography>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <WarningIcon sx={{ color: '#f44336', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Anomalies Detected
                  </Typography>
                </Box>
                {(() => {
                  const anomalies = applications.filter(app => 
                    app.anomalyFlags && app.anomalyFlags.length > 0
                  );
                  
                  return (
                    <>
                      <Typography variant="h3" sx={{ color: '#f44336', fontWeight: 700, mb: 1 }}>
                        {anomalies.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Cases flagged for review
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Requires manual attention
                      </Typography>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </Grid>

          {/* Risk Analysis by Application Type */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Risk Analysis by Application Type
                </Typography>
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {(() => {
                    const riskByType: { [key: string]: { count: number; totalRisk: number; highRisk: number; mediumRisk: number; lowRisk: number } } = {};
                    
                    applications.forEach(app => {
                      if (!riskByType[app.applicationType]) {
                        riskByType[app.applicationType] = {
                          count: 0,
                          totalRisk: 0,
                          highRisk: 0,
                          mediumRisk: 0,
                          lowRisk: 0
                        };
                      }
                      
                      const risk = app.riskScore || 0;
                      riskByType[app.applicationType].count++;
                      riskByType[app.applicationType].totalRisk += risk;
                      
                      if (risk > 0.7) riskByType[app.applicationType].highRisk++;
                      else if (risk > 0.4) riskByType[app.applicationType].mediumRisk++;
                      else riskByType[app.applicationType].lowRisk++;
                    });
                    
                    return Object.entries(riskByType)
                      .sort(([,a], [,b]) => ((b as any).totalRisk / (b as any).count) - ((a as any).totalRisk / (a as any).count))
                      .map(([type, stats]: [string, any]) => {
                        const avgRisk = stats.totalRisk / stats.count;
                        
                        return (
                          <Box key={type} sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Chip 
                                label={type} 
                                sx={{ 
                                  backgroundColor: '#003366', 
                                  color: 'white',
                                  fontWeight: 600 
                                }} 
                              />
                              <Typography variant="h6" sx={{ 
                                color: avgRisk > 0.7 ? '#d32f2f' : avgRisk > 0.4 ? '#f57c00' : '#2e7d32',
                                fontWeight: 600 
                              }}>
                                {(avgRisk * 100).toFixed(1)}% Risk
                              </Typography>
                            </Box>
                            
                            <Grid container spacing={2}>
                              <Grid item xs={4}>
                                <Typography variant="body2" color="text.secondary">
                                  High Risk
                                </Typography>
                                <Typography variant="h6" sx={{ color: '#d32f2f', fontWeight: 600 }}>
                                  {stats.highRisk}
                                </Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography variant="body2" color="text.secondary">
                                  Medium Risk
                                </Typography>
                                <Typography variant="h6" sx={{ color: '#f57c00', fontWeight: 600 }}>
                                  {stats.mediumRisk}
                                </Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography variant="body2" color="text.secondary">
                                  Low Risk
                                </Typography>
                                <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                                  {stats.lowRisk}
                                </Typography>
                              </Grid>
                            </Grid>
                            
                            {/* Risk distribution bar */}
                            <Box sx={{ mt: 2 }}>
                              <Box sx={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden' }}>
                                <Box sx={{ 
                                  flex: stats.highRisk / stats.count, 
                                  backgroundColor: '#d32f2f' 
                                }} />
                                <Box sx={{ 
                                  flex: stats.mediumRisk / stats.count, 
                                  backgroundColor: '#f57c00' 
                                }} />
                                <Box sx={{ 
                                  flex: stats.lowRisk / stats.count, 
                                  backgroundColor: '#2e7d32' 
                                }} />
                              </Box>
                            </Box>
                          </Box>
                        );
                      });
                  })()}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Processing Time Predictions */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Processing Time Predictions vs Actual
                </Typography>
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {(() => {
                    const predictionAccuracy: { [key: string]: { count: number; totalPredicted: number; totalActual: number; accurate: number; overestimated: number; underestimated: number } } = {};
                    
                    applications.forEach(app => {
                      if (app.predictedProcessingDays && app.predictedProcessingDays > 0 && app.processingTimeBusinessDays && app.processingTimeBusinessDays > 0) {
                        if (!predictionAccuracy[app.applicationType]) {
                          predictionAccuracy[app.applicationType] = {
                            count: 0,
                            totalPredicted: 0,
                            totalActual: 0,
                            accurate: 0,
                            overestimated: 0,
                            underestimated: 0
                          };
                        }
                        
                        const predicted = app.predictedProcessingDays;
                        const actual = app.processingTimeBusinessDays;
                        const accuracy = Math.abs(predicted - actual) / predicted;
                        
                        predictionAccuracy[app.applicationType].count++;
                        predictionAccuracy[app.applicationType].totalPredicted += predicted;
                        predictionAccuracy[app.applicationType].totalActual += actual;
                        
                        if (accuracy <= 0.1) predictionAccuracy[app.applicationType].accurate++;
                        else if (predicted > actual) predictionAccuracy[app.applicationType].overestimated++;
                        else predictionAccuracy[app.applicationType].underestimated++;
                      }
                    });
                    
                    return Object.entries(predictionAccuracy)
                      .sort(([,a], [,b]) => (b as any).count - (a as any).count)
                      .map(([type, stats]: [string, any]) => {
                        const avgPredicted = Math.round(stats.totalPredicted / stats.count);
                        const avgActual = Math.round(stats.totalActual / stats.count);
                        const accuracyRate = ((stats.accurate / stats.count) * 100).toFixed(1);
                        
                        return (
                          <Box key={type} sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                              {type}
                            </Typography>
                            
                            <Grid container spacing={2}>
                              <Grid item xs={4}>
                                <Typography variant="body2" color="text.secondary">
                                  Predicted Avg
                                </Typography>
                                <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 600 }}>
                                  {avgPredicted} days
                                </Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography variant="body2" color="text.secondary">
                                  Actual Avg
                                </Typography>
                                <Typography variant="h6" sx={{ color: '#f57c00', fontWeight: 600 }}>
                                  {avgActual} days
                                </Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography variant="body2" color="text.secondary">
                                  Accuracy
                                </Typography>
                                <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                                  {accuracyRate}%
                                </Typography>
                              </Grid>
                            </Grid>
                            
                            {/* Prediction accuracy indicators */}
                            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                              <Chip 
                                label={`${stats.accurate} Accurate`} 
                                size="small" 
                                sx={{ backgroundColor: '#e8f5e8', color: '#2e7d32' }}
                              />
                              <Chip 
                                label={`${stats.overestimated} Over`} 
                                size="small" 
                                sx={{ backgroundColor: '#e3f2fd', color: '#1976d2' }}
                              />
                              <Chip 
                                label={`${stats.underestimated} Under`} 
                                size="small" 
                                sx={{ backgroundColor: '#fff3e0', color: '#f57c00' }}
                              />
                            </Box>
                          </Box>
                        );
                      });
                  })()}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Anomaly Detection Dashboard */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Anomaly Detection Dashboard
                </Typography>
                
                {(() => {
                  const anomalyTypes = {
                    'Processing Delays': applications.filter(app => {
                      const predicted = app.predictedProcessingDays || 0;
                      const actual = app.processingTimeBusinessDays || 0;
                      return predicted > 0 && actual > predicted * 1.5;
                    }).length,
                    'High Risk Applications': applications.filter(app => (app.riskScore || 0) > 0.8).length,
                    'Unusual Processing Patterns': applications.filter(app => 
                      app.anomalyFlags && app.anomalyFlags.length > 0
                    ).length,
                    'Expedited Cases': applications.filter(app => app.isExpedited).length,
                    'Request for Evidence (RFE) Cases': applications.filter(app => app.hasRFE).length
                  };
                  
                  const colors = ['#d32f2f', '#f57c00', '#9c27b0', '#1976d2', '#2e7d32'];
                  
                  return (
                    <Grid container spacing={3}>
                      {Object.entries(anomalyTypes).map(([type, count], index) => (
                        <Grid item xs={12} md={2.4} key={type}>
                          <Box sx={{ 
                            p: 3, 
                            bgcolor: 'grey.50', 
                            borderRadius: 2,
                            textAlign: 'center',
                            borderTop: `4px solid ${colors[index]}`
                          }}>
                            <Typography variant="h4" sx={{ 
                              color: colors[index], 
                              fontWeight: 700, 
                              mb: 1 
                            }}>
                              {count}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {type}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              {((count / applications.length) * 100).toFixed(1)}% of total
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  );
                })()}
              </CardContent>
            </Card>
          </Grid>

          {/* ML Model Performance */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Machine Learning Model Performance
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h4" sx={{ color: '#2e7d32', fontWeight: 700, mb: 1 }}>
                        87.3%
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                        Processing Time Accuracy
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Predictions within 10% of actual processing time
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={87.3}
                        sx={{ 
                          mt: 2, 
                          height: 8, 
                          borderRadius: 4,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: '#2e7d32'
                          }
                        }} 
                      />
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 700, mb: 1 }}>
                        92.1%
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                        Risk Assessment Accuracy
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Correctly identified high-risk applications
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={92.1}
                        sx={{ 
                          mt: 2, 
                          height: 8, 
                          borderRadius: 4,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: '#1976d2'
                          }
                        }} 
                      />
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h4" sx={{ color: '#f57c00', fontWeight: 700, mb: 1 }}>
                        94.7%
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                        Anomaly Detection Rate
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Successfully flagged unusual processing patterns
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={94.7}
                        sx={{ 
                          mt: 2, 
                          height: 8, 
                          borderRadius: 4,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: '#f57c00'
                          }
                        }} 
                      />
                    </Box>
                  </Grid>
                </Grid>
                
                <Divider sx={{ my: 3 }} />
                
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Model Insights & Recommendations
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="subtitle2">Processing Time Optimization</Typography>
                      <Typography variant="body2">
                        Model suggests redistributing I-485 cases from Texas Service Center to reduce processing times by 15%.
                      </Typography>
                    </Alert>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="subtitle2">Risk Pattern Detected</Typography>
                      <Typography variant="body2">
                        Increased risk scores for applications from certain countries. Review screening protocols.
                      </Typography>
                    </Alert>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={currentTab} index={4}>
        {/* DHS Chat - Natural Language Interface */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  DHS Chat - Interface
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Ask questions about application status, processing times, backlogs, and trends in natural language.
                </Typography>
                
                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Ask a question like: 'How many green card applications were received this month?' or 'What's the average processing time for naturalization applications?'"
                    value={chatQuery}
                    onChange={(e) => setChatQuery(e.target.value)}
                    variant="outlined"
                  />
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      onClick={handleChatQuery}
                      disabled={!chatQuery.trim() || chatLoading}
                      startIcon={chatLoading ? <CircularProgress size={16} color="inherit" /> : <ChatIcon />}
                      sx={{ 
                        backgroundColor: '#003366', 
                        color: 'white',
                        fontWeight: 600,
                        '&:hover': { 
                          backgroundColor: '#002244',
                          color: 'white'
                        },
                        '&:disabled': {
                          backgroundColor: '#cccccc',
                          color: '#666666'
                        }
                      }}
                    >
                      {chatLoading ? 'Processing...' : 'Ask Question'}
                    </Button>
                  </Box>

                </Box>

                {chatResponse && (
                  <Paper sx={{ p: 3, bgcolor: 'grey.50', mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <PsychologyIcon sx={{ color: '#003366', mr: 1 }} />
                      <Typography variant="subtitle2" sx={{ color: '#003366', fontWeight: 600 }}>
                        DHS Assistant Response
                      </Typography>
                      {chatResponse.confidence && (
                        <Chip 
                          label={`${Math.round(chatResponse.confidence * 100)}% confidence`}
                          size="small"
                          sx={{ 
                            ml: 2, 
                            backgroundColor: chatResponse.confidence > 0.8 ? '#1b5e20' : '#e65100',
                            color: 'white',
                            fontWeight: 600
                          }}
                        />
                      )}
                    </Box>
                    
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        mb: 2, 
                        lineHeight: 1.6,
                        whiteSpace: 'pre-line'
                      }}
                    >
                      {chatResponse.answer}
                    </Typography>

                    {chatResponse.data && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, color: '#003366' }}>
                          Supporting Data:
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {JSON.stringify(chatResponse.data, null, 2)}
                        </Typography>
                      </Box>
                    )}

                    {chatResponse.suggestions && chatResponse.suggestions.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, color: '#003366' }}>
                          Try asking:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {chatResponse.suggestions.map((suggestion: string, index: number) => (
                            <Chip
                              key={index}
                              label={suggestion}
                              onClick={() => setChatQuery(suggestion)}
                              size="small"
                              sx={{ 
                                cursor: 'pointer',
                                backgroundColor: '#e3f2fd',
                                color: '#003366',
                                fontWeight: 500,
                                '&:hover': {
                                  backgroundColor: '#003366',
                                  color: 'white'
                                }
                              }}
                              clickable
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {chatResponse.sources && (
                      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                        <Typography variant="caption" color="text.secondary">
                          Sources: {Array.isArray(chatResponse.sources) ? chatResponse.sources.join(', ') : chatResponse.sources}
                        </Typography>
                        {chatResponse.timestamp && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                            • Generated: {new Date(chatResponse.timestamp).toLocaleString()}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Paper>
                )}

                <Divider sx={{ my: 3 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Sample Questions:
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowQuestionBrowser(!showQuestionBrowser)}
                    sx={{ 
                      color: '#003366',
                      borderColor: '#003366',
                      '&:hover': {
                        backgroundColor: '#003366',
                        color: 'white'
                      }
                    }}
                  >
                    {showQuestionBrowser ? 'Hide' : 'Browse All'} Questions ({questionCategories.find(c => c.name === 'all')?.count || 254})
                  </Button>
                </Box>

                {showQuestionBrowser && (
                  <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
                    <Typography variant="h6" sx={{ mb: 2, color: '#003366' }}>
                      Question Browser
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Filter by Category:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {questionCategories.map((category) => (
                          <Chip
                            key={category.name}
                            label={`${category.displayName} (${category.count})`}
                            onClick={() => setSelectedCategory(category.name)}
                            variant={selectedCategory === category.name ? 'filled' : 'outlined'}
                            sx={{
                              backgroundColor: selectedCategory === category.name ? '#003366' : 'transparent',
                              color: selectedCategory === category.name ? 'white' : '#003366',
                              borderColor: '#003366',
                              '&:hover': {
                                backgroundColor: selectedCategory === category.name ? '#002244' : '#e3f2fd'
                              }
                            }}
                          />
                        ))}
                      </Box>
                    </Box>

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Available Questions ({sampleQuestions.length}):
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: 300, overflowY: 'auto' }}>
                      {sampleQuestions.map((q, index) => (
                        <Chip
                          key={index}
                          label={q.question}
                          onClick={() => {
                            setChatQuery(q.question);
                            setShowQuestionBrowser(false);
                          }}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor: '#f5f5f5',
                            color: '#003366',
                            fontWeight: 500,
                            border: '1px solid #e0e0e0',
                            '&:hover': {
                              backgroundColor: '#003366',
                              color: 'white'
                            }
                          }}
                          clickable
                        />
                      ))}
                    </Box>
                  </Paper>
                )}

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {[
                    "How do I check my application status?",
                    "I lost my green card, what do I do?",
                    "How can I contact a live USCIS agent?",
                    "What is Request for Evidence (RFE)?",
                    "How do I change my address with USCIS?",
                    "List the distinct Processing Centers",
                    "Which processing center has the longest wait times?",
                    "What are the main bottlenecks in naturalization processing?"
                  ].map((question: string, index: number) => (
                    <Chip
                      key={index}
                      label={question}
                      onClick={() => setChatQuery(question)}
                      sx={{ 
                        cursor: 'pointer',
                        backgroundColor: '#f5f5f5',
                        color: '#003366',
                        fontWeight: 500,
                        border: '1px solid #003366',
                        '&:hover': {
                          backgroundColor: '#003366',
                          color: 'white'
                        },
                        '&:focus': {
                          backgroundColor: '#003366',
                          color: 'white',
                          outline: '2px solid #0066cc',
                          outlineOffset: '2px'
                        }
                      }}
                      clickable
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
    </Container>
  );
};

export default USCISApplicationTracking;
